import type { WorkItem } from '../types/work'
import { asset, models, pipelineFrames } from './assets'
import { PIPELINE_PROMPT } from './prompt'

export const work: WorkItem[] = [
  {
    slug: '3d-avatars',
    title: '3D Avatars',
    summary:
      'An AI pipeline that turns a selfie into a rigged, animated 3D character, with customizable styles.',
    role: 'Build & write-up',
    year: '2026',
    detail: {
      tagline:
        'An AI pipeline that turns a selfie into a rigged, animated 3D character, with customizable styles.',
      blocks: [
        {
          kind: 'prose',
          body: [
            "I recently saw Ridd and Tommy's [Backyard Designers](https://backyarddesigners.club/). I instantly fell in love with the art style. They felt 3D, but when I poked around, it was actually just clever CSS making them feel 3D.",
            'Which led me to wondering, what would it take to _actually_ make them 3D?',
          ],
        },
        {
          kind: 'prose',
          heading: 'The pipeline',
          body: [
            'After chatting with Claude for a bit, I had a tentative idea for how this could work. Honestly, it is mostly plumbing information between API calls. It runs on gpt-image-2, Meshy, a local Blender pass, and three.js to render the result.',
            'It starts by turning a photograph into three consistent views. The front is generated first, then the side and back are generated with that finished front passed back in as a reference.',
          ],
        },
        {
          kind: 'figure',
          figure: {
            src: asset('stylize.jpg'),
            alt: 'Three generated views of the same character: front, side and back',
            caption: 'Stage 01. The critical step of getting the right style in place.',
            tone: 'full',
            aspect: 'auto',
          },
        },
        {
          kind: 'prose',
          body: [
            'Those three views go to a multi-image reconstruction, and what comes back is a real mesh. Meshy does all the heavy lifting here.',
          ],
        },
        {
          kind: 'model',
          src: models.ted,
          frames: pipelineFrames,
          caption: 'Feels like magic!',
        },
        {
          kind: 'steps',
          steps: [
            {
              label: '01',
              title: 'Stylize',
              body:
                'Reference images plus a photo of the subject go to gpt-image-2, front view first, then side and back generated against it.',
            },
            {
              label: '02',
              title: 'Reconstruct',
              body:
                "The three views go to Meshy's multi-image-to-3D, front first, so the back and sides come from real input.",
            },
            {
              label: '03',
              title: 'Rig',
              body: "Meshy's auto-rigger binds a skeleton. This mostly works.",
            },
            {
              label: '04',
              title: 'Finish',
              body:
                'Locally, via the Blender MCP: repair the material, recompute the normals, paint the hand detail, and rebuild the skeleton so every character shares one hierarchy.',
            },
            {
              label: '05',
              title: 'Animate',
              body:
                'A banked clip plays on the result. Because the hierarchy is shared, the same clip drives every avatar the pipeline has produced.',
            },
          ],
        },
        {
          kind: 'prompt',
          heading: 'If you want to do this yourself',
          body: [
            'Here is a prompt you can give your coding agent. You will need a Meshy API key, an OpenAI API key, and the Blender MCP configured for this prompt specifically. There are many alternatives to these tools.',
          ],
          label: 'Pipeline prompt',
          text: PIPELINE_PROMPT,
        },
        {
          kind: 'prose',
          heading: 'On Consistency',
          body: [
            'The main enemy was consistency between avatars, and specifically the art style. Some came back with realistic eyes — whites and a pupil — while others were the flat anime-ish eye you see in the finished characters.',
            "Almost all of that is decided at the first step. It's where I spent a lot of time prompt-tuning, re-running generations, but honestly still haven't gotten it to a point that it works consistently well. A better image model would probably fix most of this on its own.",
          ],
        },
        {
          kind: 'figure',
          figure: {
            src: asset('drift.jpg'),
            alt:
              'Six generated faces of the same two people, showing differences in eye treatment and facial proportion between runs',
            caption:
              'The same two people, different runs. The top row drifts on eye treatment — whites and an iris, then dark, then the flat black that shipped. The bottom row drifts on proportion instead: the face gets rounder and younger between attempts that used the same prompt.',
            tone: 'full',
            aspect: 'auto',
          },
        },
        {
          kind: 'prose',
          heading: 'On a generic-avatar',
          body: [
            'The other inconsistency was rigging. It was slow, and it felt excessive to rig every avatar individually for every motion I wanted.',
            'For example, if I wanted an action of "solving a Rubik\'s cube", I\'d have to rig each avatar individually because of different character dimensions.',
            'The engineer in me wanted to optimize: what if we just had a generic avatar that had excellent rigging, and each character just applied styles and accessories?',
            "I tried this, but the quality of the characters just wasn't good enough. Getting each avatar to feel unique and its own just didn't feel right. But if I could have spent more time on this project, this is where I would love to explore more.",
          ],
        },
        {
          kind: 'figure',
          figure: {
            src: asset('skinned.jpg'),
            alt:
              "The same character's head twice — on the left his own face, on the right another person's face projected onto it, with ghost glasses, doubled eyebrows and a doubled mouth",
            caption:
              'Results from a shared avatar. It was hard keeping the details while maintaining a consistent face structure. As you can tell, these results were not great.',
            tone: 'full',
            aspect: 'auto',
          },
        },
        {
          kind: 'prose',
          body: [
            'I did keep a little bit of this pipeline. While each character is rigged separately, each rig ends up with the same bones under the same names in the same order. So one clip fundamentally drives the same one, albeit the details might differ between each rig.',
          ],
        },
        {
          kind: 'model',
          src: models.alex,
          stages: ['walk'],
          // No stills exist for this character, and Ted's would be the wrong
          // person. An empty array makes the block omit itself instead.
          frames: [],
          caption: 'Same clip, different characters.',
        },
        {
          kind: 'list',
          heading: 'On what is next',
          body: [
            "I was quite pleased with the results. It took longer than I expected, and the pipeline is slow. I think there's a lot of open field to run with this project though. Things I want to continue exploring:",
          ],
          items: [
            'Shared rigged model',
            'Better rigging (fingers, more joints, etc)',
            'Better and more consistent image generation',
          ],
        },
        {
          kind: 'prose',
          body: ['If you end up taking this further, would love to see it!'],
        },
      ],
    },
  },
]

export function getWorkItem(slug: string | undefined): WorkItem | undefined {
  return work.find((item) => item.slug === slug)
}
